import { useEffect, useImperativeHandle, useRef, type ReactNode, type Ref, type RefObject } from 'react'
import { Box, Flex, type BoxProps } from '@chakra-ui/react'

type AccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string }

export type ModalPlacement = 'top' | 'center' | 'end'

export type ModalLayerProps = AccessibleName &
  Omit<BoxProps, 'ref' | 'role' | 'aria-label' | 'aria-labelledby' | 'zIndex' | 'onKeyDown' | 'children'> & {
    open: boolean
    onClose: () => void
    children: ReactNode
    ref?: Ref<HTMLDivElement>
    placement?: ModalPlacement
    role?: 'dialog' | 'alertdialog'
    /** Gates scrim-click and Escape dismissal, e.g. during a save. */
    dismissible?: boolean
    /** Focused on open; defaults to the panel. */
    initialFocusRef?: RefObject<HTMLElement | null>
    /** Preserve content while closed, hidden from focus and interaction. */
    keepMounted?: boolean
    /** Scrim layer; the panel sits 50 above it. */
    zIndex?: number
    backdropProps?: BoxProps
  }

const POSITIONER: Record<ModalPlacement, BoxProps> = {
  top: { alignItems: 'flex-start', justifyContent: 'center', pt: 16, px: 4 },
  center: { alignItems: 'center', justifyContent: 'center', p: { base: 4, md: 8 } },
  end: { alignItems: 'stretch', justifyContent: 'flex-end' },
}

/**
 * The app's only modal primitive. React owns the scrim and panel outright and never locks,
 * hides or restyles anything outside them. Chakra's Dialog/Drawer do, and leak those
 * locks under StrictMode (docs/superpowers/specs/2026-09-24-modal-layer-design.md).
 */
export function ModalLayer({
  open,
  onClose,
  children,
  ref,
  placement = 'top',
  role = 'dialog',
  dismissible = true,
  initialFocusRef,
  keepMounted = false,
  zIndex = 1400,
  backdropProps,
  ...panelProps
}: ModalLayerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(ref, () => panelRef.current as HTMLDivElement)

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    const previousFocus = document.activeElement
    if (!panel?.contains(previousFocus)) (initialFocusRef?.current ?? panel)?.focus({ preventScroll: true })
    return () => {
      // Closing a background layer must not steal focus from another layer or field.
      const focusStillHere =
        panel?.contains(document.activeElement) || document.activeElement === document.body
      if (focusStillHere && previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus({ preventScroll: true })
      }
    }
  }, [open, initialFocusRef])

  if (!open && !keepMounted) return null

  return (
    <>
      {open && (
        <Box
          data-testid="modal-layer-scrim"
          aria-hidden="true"
          position="fixed"
          inset={0}
          zIndex={zIndex}
          bg="blackAlpha.500"
          animationName="fade-in"
          animationDuration="slow"
          {...backdropProps}
          onClick={() => {
            if (dismissible) onClose()
          }}
        />
      )}
      {/* @ts-expect-error Chakra v3: BoxProps.direction (CSS ltr/rtl) clashes with Flex's flexDirection alias */}
      <Flex position="fixed" inset={0} zIndex={zIndex + 50} pointerEvents="none" {...POSITIONER[placement]}>
        <Box
          position="relative"
          outline="none"
          {...panelProps}
          ref={panelRef}
          role={role}
          aria-modal="true"
          aria-hidden={!open}
          inert={!open}
          tabIndex={-1}
          pointerEvents={open ? 'auto' : 'none'}
          onKeyDown={(event) => {
            if (open && event.key === 'Escape' && !event.defaultPrevented) {
              event.stopPropagation()
              if (dismissible) onClose()
            }
          }}
        >
          {children}
        </Box>
      </Flex>
    </>
  )
}
